import { dispatchPopover } from "./canvasSelectLabel";
import { canvasObjectTarget } from "./canvasObjectTarget";

const TYPES_WITHOUT_GENERIC_CLICK = [
  "row",
  "collapse",
  "tabs",
  "carousel",
  "conditional_row",
  "product_hero",
  "navigation",
];

export function attachElementCanvasDelegation(canvas, getElement, getSelected) {
  if (!canvas) return () => {};

  const findContext = (target) => {
    const resolved = canvasObjectTarget(canvas, target);
    if (resolved?.type !== "elements") return null;
    const tag = resolved.target;
    const id = tag.getAttribute("data-element-id");
    const element = getElement(id);
    if (!element || element.toBeDeleted) return null;
    return { tag, element };
  };

  let pendingSelection = null;
  const cancelPendingSelection = () => {
    clearTimeout(pendingSelection);
    pendingSelection = null;
  };

  const selectAfterControlUpdate = (element) => {
    cancelPendingSelection();
    pendingSelection = setTimeout(() => {
      pendingSelection = null;
      dispatchCustomEvent("selectObject", {
        object_type: "elements",
        object_id: element.id,
      });
    }, 0);
  };

  const onClick = (e) => {
    cancelPendingSelection();
    const ctx = findContext(e.target);
    if (!ctx) return;
    const { tag, element } = ctx;
    const type = element.element_type;

    if (e.target.closest("a")) e.preventDefault();

    if (type === "row") return;

    if (type === "product_hero" || type === "navigation") {
      dispatchCustomEvent("selectObject", {
        object_type: "elements",
        object_id: element.id,
        tab: "Content",
        panel_closed: false,
      });
      return;
    }

    if (type === "conditional_row") {
      e.preventDefault();
      dispatchCustomEvent("selectObject", {
        object_type: "elements",
        object_id: element.id,
      });
      return;
    }

    if (type === "carousel") {
      e.preventDefault();
      dispatchCustomEvent("selectObject", {
        object_type: "elements",
        object_id: element.id,
      });
      return;
    }

    if (type === "collapse") {
      const toggle = e.target.closest(".cf-collapse-toggle");
      if (
        toggle &&
        toggle.getAttribute("data-list-item-element-id") == element.id
      ) {
        e.preventDefault();
        selectAfterControlUpdate(element);
      }
      return;
    }

    if (type === "tabs") {
      const tab = e.target.closest(".cf-tab-item");
      if (tab && tab.getAttribute("data-tab-element-id") == element.id) {
        e.preventDefault();
        selectAfterControlUpdate(element);
      }
      return;
    }

    const sel = getSelected();
    const isHeadlineOrText = type === "headline" || type === "text";
    if (
      sel &&
      sel.object_type == "elements" &&
      sel.object_id == element.id &&
      isHeadlineOrText
    ) {
      if (sel.editing !== true && !tag.querySelector(".fr-inline")) {
        dispatchCustomEvent("selectObject", {
          object_type: "elements",
          object_id: element.id,
          collapse: null,
          editing: true,
          target: e.target,
        });
      }
    } else {
      dispatchCustomEvent("selectObject", {
        object_type: "elements",
        object_id: element.id,
        tab: "Content",
        panel_closed: isHeadlineOrText && window.panel_closed == true,
      });
    }
  };

  const onContextMenu = (e) => {
    const ctx = findContext(e.target);
    if (!ctx) return;
    const { element } = ctx;
    const type = element.element_type;

    if (type === "row") return;

    if (type === "conditional_row") {
      e.preventDefault();
      dispatchPopover({
        object_type: "elements",
        object_id: element.id,
        tab: "Content",
        object: element,
        anchorPosition: { x: e.clientX, y: e.clientY },
      });
      return;
    }

    if (type === "carousel") {
      e.preventDefault();
      dispatchPopover({
        object_type: "elements",
        object_id: element.id,
        tab: "Content",
        object: element,
        anchorPosition: { x: e.clientX, y: e.clientY },
      });
      return;
    }

    if (type === "product_hero" || type === "navigation") {
      e.preventDefault();
      const customElementSection = e.target.closest(
        ".cf-product-custom-element, .cf-navigation-custom-element"
      );
      const toggleItemId = customElementSection
        ? parseInt(
            customElementSection.getAttribute("data-content-toggle-item-id")
          )
        : null;

      if (toggleItemId) {
        dispatchPopover({
          object_type: "content_toggle_items",
          object_id: toggleItemId,
          tab: "Content",
          object: undefined,
          anchorPosition: { x: e.clientX, y: e.clientY },
        });
        return;
      }

      dispatchPopover({
        object_type: "elements",
        object_id: element.id,
        tab: "Content",
        object: element,
        anchorPosition: { x: e.clientX, y: e.clientY },
      });
      return;
    }

    if (type === "collapse" || type === "tabs") return;

    e.preventDefault();
    dispatchPopover({
      object_type: "elements",
      object_id: element.id,
      tab: "Content",
      object: element,
      anchorPosition: { x: e.clientX, y: e.clientY },
    });
  };

  const onDblClick = (e) => {
    const ctx = findContext(e.target);
    if (!ctx) return;
    const { tag, element } = ctx;
    const type = element.element_type;

    if (TYPES_WITHOUT_GENERIC_CLICK.includes(type)) return;

    const sel = getSelected();
    const isInlineEditable = ["headline", "text", "button"].includes(type);
    const isHeadlineOrText = type === "headline" || type === "text";

    if (
      isInlineEditable &&
      !tag.querySelector(".fr-inline") &&
      (!sel ||
        sel.object_type !== "elements" ||
        sel.object_id !== element.id ||
        sel.editing == false)
    ) {
      dispatchCustomEvent("selectObject", {
        object_type: "elements",
        object_id: element.id,
        collapse: null,
        editing: true,
        target: e.target,
        panel_closed: isHeadlineOrText && window.panel_closed == true,
      });
    }

    if (type === "html") {
      dispatchCustomEvent("selectObject", {
        object_type: "elements",
        object_id: element.id,
        collapse: null,
        editing: false,
        panel_closed: false,
      });
      const modalTrigger = document.querySelector(
        '.setting[data-setting_type="code"][data-object_type="elements"][data-object_id="' +
          element.id +
          '"] button[setting_name="[html]"]'
      );
      if (modalTrigger) modalTrigger.click();
    }
  };

  canvas.addEventListener("click", onClick);
  canvas.addEventListener("contextmenu", onContextMenu);
  canvas.addEventListener("dblclick", onDblClick);
  document.addEventListener("selectObject", cancelPendingSelection);

  return () => {
    cancelPendingSelection();
    canvas.removeEventListener("click", onClick);
    canvas.removeEventListener("contextmenu", onContextMenu);
    canvas.removeEventListener("dblclick", onDblClick);
    document.removeEventListener("selectObject", cancelPendingSelection);
  };
}
